<template>
  <div class="clipboard-view">
    <div v-if="loading" class="loading">
      <p>Loading clipboard...</p>
    </div>
    
    <div v-else-if="error" class="error">
      <h2>Error</h2>
      <p>{{ error }}</p>
      <button @click="$router.push('/')" class="home-btn">Go to Home</button>
    </div>
    
    <div v-else class="clipboard-container">
      <div class="clipboard-header">
        <div class="header-top">
          <div class="header-title">
        <h1 v-if="!isEditingName" @click="startEditingName" class="clipboard-name">
          {{ clipboardName }}
              <span class="edit-icon">
                <Icon name="edit" :size="18" />
              </span>
        </h1>
        <div v-else class="edit-name-form">
              <div class="edit-input-container">
          <input 
            v-model="editNameValue" 
            class="edit-name-input"
            ref="nameInput"
            @keyup.enter="updateClipboardName"
            @keyup.esc="cancelEditingName"
          />
          <div class="edit-actions">
            <button @click="updateClipboardName" class="save-btn">Save</button>
            <button @click="cancelEditingName" class="cancel-btn">Cancel</button>
          </div>
              </div>
            </div>
          </div>
          <button v-if="!isEditingName" @click="goHome" class="home-btn-top" title="Go to Home">
            <Icon name="home" :size="20" />
          </button>
        </div>
        <div class="clipboard-info">
          <p>Clipboard ID: <span class="clipboard-id">{{ clipboardId }}</span></p>
          
          <div class="link-container">
            <div class="link-preview">{{ currentUrl }}</div>
            <button @click="copyClipboardLink" class="copy-link-btn" title="Copy clipboard link">
              <span v-if="!linkCopied">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"></path>
                  <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"></path>
                </svg>
              </span>
              <span v-else class="copy-success">
                <svg class="checkmark" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="16" height="16">
                  <path fill="none" stroke="currentColor" stroke-width="3" d="M5 13l4 4L19 7"></path>
                </svg>
              </span>
            </button>
          </div>
          </div>
          
        <!-- Clipboard Content Section -->
        <div class="clipboard-content-section" v-if="clipboardContent || isEditingContent">
          <div class="content-header">
            <h3>Description</h3>
            <button v-if="!isEditingContent" @click="startEditingContent" class="edit-content-btn" title="Edit description">
              <Icon name="edit" :size="14" />
            </button>
          </div>
          <div v-if="!isEditingContent" class="content-display">
            <p>{{ clipboardContent }}</p>
          </div>
          <div v-else class="content-edit">
            <textarea 
              v-model="editContentValue" 
              class="content-textarea"
              placeholder="Enter clipboard description..."
              rows="3"
              ref="contentTextarea"
            ></textarea>
            <div class="content-actions">
              <button @click="updateClipboardContent" class="save-content-btn">Save</button>
              <button @click="cancelEditingContent" class="cancel-content-btn">Cancel</button>
            </div>
          </div>
        </div>
        <div class="clipboard-content-section" v-else>
          <button @click="startEditingContent" class="add-content-btn">
            <Icon name="plus" :size="14" />
            Add Description
          </button>
        </div>
      </div>
      
      <div class="tabs-navigation">
        <button 
          :class="['tab-button', { active: activeTab === 'text' }]"
          @click="switchToTextTab"
        >
          <Icon name="file-text" :size="16" />
          <span class="tab-label">Text ({{ entries.length }})</span>
        </button>
        <button 
          :class="['tab-button', { active: activeTab === 'files' }]"
          @click="switchToFilesTab"
        >
          <Icon name="folder" :size="16" />
          <span class="tab-label">Files ({{ fileCount }})</span>
        </button>
        
      </div>

      <div v-show="activeTab === 'files'" class="tab-content">
        <FileUploader :clipboardId="clipboardId" ref="fileUploader" @file-count-changed="updateFileCount" />
      </div>
      
      <div v-show="activeTab === 'text'" class="tab-content">
        <div class="form-container">
          <div class="editor-toolbar toolbar-flex">
            <div class="toolbar-left">
              <label for="lang-select">Lang:</label>
              <select id="lang-select" v-model="selectedLang" @change="onLangChange">
                <option v-for="opt in langOptions" :key="opt.value" :value="opt.value">
                  {{ opt.label }}
                </option>
              </select>
            </div>
            <div class="toolbar-right">
              <button 
                @click="addClipboardEntry" 
                class="save-button toolbar-btn" 
                :class="{ 'disabled': !newContent.trim(), 'active': newContent.trim() }"
                :disabled="!newContent.trim()"
                :title="newContent.trim() ? 'Save entry' : 'Enter content to save'"
              >
                <Icon name="save" :size="16" />
              </button>
              <button 
                @click="openExpandEditor" 
                class="expand-button toolbar-btn"
                title="Expand editor"
              >
                <Icon name="maximize" :size="16" />
              </button>
            </div>
          </div>
          <div class="entry-name-input">
            <input 
              v-model="newEntryName" 
              placeholder="Entry name (optional)" 
              class="name-input"
            />
          </div>
          <div class="editor-container">
            <Codemirror 
              v-model:value="newContent" 
              :options="{ ...cmOptions, mode: selectedLang }"
              :border="false"
              ref="cmRef"
              height="200px"
              placeholder="Paste your text here..."
              @ready="onReady"
            />
          </div>
        </div>
        
        <div class="entries-container">
          <div v-if="entries.length === 0" class="empty-message">
            <p>No entries yet. Paste something to get started!</p>
          </div>
          <div v-else class="entries-list">
            <div v-for="entry in entries" :key="entry.unique_id" class="entry-card">
              <div class="entry-header">
                <div class="entry-info">
                  <div class="entry-title-row">
                    <span v-if="entry.name" class="entry-name">{{ entry.name }}</span>
                    <span v-if="entry.content_type" 
                          class="entry-type" 
                          :style="{ backgroundColor: getLanguageColor(entry.content_type) }">
                      {{ getLanguageLabel(entry.content_type) }}
                    </span>
                  </div>
                  <span class="entry-date">Last updated at {{ entry.updated_at }}</span>
                </div>
                <div class="entry-actions">
                  <button @click="openEditModal(entry)" class="edit-button" title="Edit entry">
                    <Icon name="edit" :size="16" />
                  </button>
                  <button @click="copyToClipboard(entry.content, entry.unique_id)" class="copy-button" title="Copy to clipboard">
                    <span v-if="!copiedEntries[entry.unique_id]">
                      <Icon name="copy" :size="16" />
                    </span>
                    <span v-else class="copy-success">
                      <Icon name="check" :size="16" :stroke-width="3" />
                    </span>
                  </button>
                  <!-- <button @click="viewEntry(entry.unique_id)" class="view-button" title="View Entry">
                    <Icon name="external-link" :size="16" />
                  </button> -->
                  <button @click="confirmDelete(entry.unique_id)" class="delete-button" title="Delete entry">
                    <Icon name="delete" :size="16" />
                  </button>
                </div>
              </div>
              <div class="entry-content">
                <!-- All content types use CodeMirror for security - no HTML rendering -->
                <div class="entry-content-display" @dblclick="openEditModal(entry)">
                  <Codemirror 
                    :value="entry.content" 
                    :options="getDisplayOptions(entry.content_type)"
                    :border="false"
                    height="100px"
                    :disabled="true"
                  />
                </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      
      
      </div>
      
    <!-- Modal for editing content -->
    <div v-if="modalVisible" class="modal-overlay">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <div class="modal-title-section">
            <div class="modal-name-input">
              <input 
                v-model="modalEntryName" 
                placeholder="Entry name (optional)" 
                class="modal-name-field"
              />
      </div>
            <div class="modal-controls-row">
              <div class="modal-lang-selector">
                <label for="modal-lang-select">Language:</label>
                <select id="modal-lang-select" v-model="modalSelectedLang" @change="onModalLangChange">
                  <option v-for="opt in langOptions" :key="opt.value" :value="opt.value">{{ opt.label }}</option>
                </select>
              </div>
              
              <!-- JSON formatting buttons -->
              <div v-if="modalSelectedLang === 'javascript'" class="json-controls">
                <button @click="formatJSON" class="json-btn format-btn" title="Format JSON">
                  <Icon name="code" :size="16" />
                  <span>Format</span>
                </button>
                <button @click="compressJSON" class="json-btn compress-btn" title="Compress JSON">
                  <Icon name="minimize" :size="16" />
                  <span>Compress</span>
                </button>
                <button @click="escapeJSON" class="json-btn escape-btn" title="Escape JSON">
                  <Icon name="quote" :size="16" />
                  <span>Escape</span>
                </button>
                <button @click="unescapeJSON" class="json-btn unescape-btn" title="Unescape JSON">
                  <Icon name="unquote" :size="16" />
                  <span>Unescape</span>
                </button>
              </div>
              
              <!-- Preview button for markdown - opens in new tab -->
              <!-- <button 
                v-if= "modalEntry && modalEntry.unique_id && modalEntry.unique_id !== 'temp-expand'" 
                @click="previewInNewTab" 
                class="preview-toggle-btn"
                title="Preview in New Tab (Secure)"
              >
                <Icon name="external-link" :size="16" />
                <span>Preview</span>
              </button> -->
            </div>
          </div>
          <button @click="closeModal" class="modal-close">
            <Icon name="close" :size="20" />
          </button>
        </div>
        <div class="modal-body">
          <!-- Edit Mode Only - No HTML rendering for security -->
          <Codemirror 
            v-if="modalEntry"
            v-model:value="modalEntryContent" 
            :options="{ ...modalCmOptions, mode: modalSelectedLang }"
            :border="false"
            height="100%"
            ref="modalCmRef"
            @ready="onModalReady"
          />
        </div>
        <div class="modal-footer">
          <button @click="saveModalEntry" 
                  class="modal-save-btn" 
                  :class="{ 
                    'saving': saveButtonState === 'saving', 
                    'success': saveButtonState === 'success' 
                  }"
                  :disabled="saveButtonState === 'saving'">
            <span v-if="saveButtonState === 'normal'">Save</span>
            <span v-else-if="saveButtonState === 'saving'">
              <span class="spinner"></span>
              Saving...
            </span>
            <span v-else-if="saveButtonState === 'success'">
              <Icon name="check" :size="16" />
              Saved!
            </span>
          </button>
          <button @click="closeModal" class="modal-cancel-btn">Cancel</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { clipboardService, fileService } from '@/services/api';
import { historyService } from '@/services/historyService';
import FileUploader from '@/components/FileUploader.vue';
import Icon from '@/components/Icon.vue';
import "codemirror/mode/markdown/markdown.js";
import "codemirror/mode/sql/sql.js";
import "codemirror/mode/shell/shell.js";
import "codemirror/mode/javascript/javascript.js";
import "codemirror/mode/htmlmixed/htmlmixed.js";
import "codemirror/mode/css/css.js";
import "codemirror/mode/xml/xml.js";
import "codemirror/mode/python/python.js";
import "codemirror/mode/yaml/yaml.js";
import "codemirror/mode/groovy/groovy.js";
import "codemirror/mode/clike/clike.js";
import "codemirror/addon/hint/show-hint.js";
import "codemirror/addon/hint/show-hint.css";
import Codemirror from "codemirror-editor-vue3";
import { markdownService } from '@/services/markdownService';
import { jsonService } from '@/services/jsonService';
// import { buildFileUrl } from '@/config';

export default {
  name: 'ClipboardView',
  components: {
    FileUploader,
    Codemirror,
    Icon
  },
  props: {
    clipboardId: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      clipboardName: 'Loading...',
      clipboardContent: '',
      editNameValue: '',
      editContentValue: '',
      isEditingName: false,
      isEditingContent: false,
      entries: [],
      newContent: '',
      newEntryName: '',
      loading: true,
      error: null,
      copiedEntries: {},
      linkCopied: false,
      modalVisible: false,
      modalEntry: null,
      modalEntryContent: '',
      modalEntryName: '',
      modalSelectedLang: 'markdown',

      currentUrl: '',
      saveButtonState: 'normal', // normal, saving, success
      activeTab: 'text',
      fileCount: 0,
      langOptions: [
        { value: 'null', label: 'Plain' },
        { value: 'markdown', label: 'Markdown' },
        { value: 'javascript', label: 'JSON' },
        { value: 'sql', label: 'SQL' },
        { value: 'clike', label: 'Java' },
        { value: 'groovy', label: 'Groovy' },
        { value: 'javascript', label: 'JavaScript' },
        { value: 'shell', label: 'Shell' },
        { value: 'htmlmixed', label: 'HTML' },
        { value: 'css', label: 'CSS' },
        { value: 'xml', label: 'XML' },
        { value: 'python', label: 'Python' },
        { value: 'yaml', label: 'YAML' }
      ],
      selectedLang: 'markdown',
      cmOptions: {
        mode: 'markdown',
        lineNumbers: true,
        lineWrapping: true,
        theme: 'default',
        tabSize: 4,
        autofocus: true,
        styleActiveLine: true
      },
      modalCmOptions: {
        mode: 'markdown',
        lineNumbers: true,
        lineWrapping: true,
        theme: 'default',
        tabSize: 4,
        autofocus: true,
        styleActiveLine: true
      },
      availableFiles: [], // Store available files for reference validation
      autocomplete: {
        visible: false,
        items: [],
        selectedIndex: 0,
        triggerPos: null,
        prefix: ''
      }
    };
  },
  computed: {
    // No computed properties needed for markdown rendering in ClipboardView
  },
  created() {
    this.fetchClipboardData();
    this.currentUrl = window.location.href;
  },
  beforeUnmount() {
    // Clean up modal hints style when component is destroyed
    this.removeModalHintsStyle();
  },
  methods: {
    async fetchClipboardData() {
      this.loading = true;
      this.error = null;
      
      try {
        // Get clipboard info
        const clipboardResponse = await clipboardService.getClipboard(this.clipboardId);
        this.clipboardName = clipboardResponse.data.clipboard.name;
        this.clipboardContent = clipboardResponse.data.clipboard.content || '';
        
        // Get clipboard entries
        const entriesResponse = await clipboardService.getEntries(this.clipboardId);
        this.entries = entriesResponse.data;
        
        // Get available files for markdown references
        await this.updateAvailableFiles();
        
        // Add to clipboard history after successful load
        historyService.addToHistory(this.clipboardId, this.clipboardName);
      } catch (err) {
        console.error('Error fetching clipboard data', err);
        this.error = 'Clipboard not found or error loading data';
      } finally {
        this.loading = false;
      }
    },
    
    async addClipboardEntry() {
      if (!this.newContent.trim()) {
        return;
      }
      
      try {
        const response = await clipboardService.addEntry(
          this.clipboardId, 
          this.newContent, 
          this.selectedLang, 
          this.newEntryName.trim()
        );
        
        // Add new entry to the beginning of the list
        this.entries.unshift(response.data);
        
        // Clear the inputs
        this.newContent = '';
        this.newEntryName = '';
      } catch (err) {
        console.error('Error adding entry', err);
        alert('Failed to add entry. Please try again.');
      }
    },
    
    async deleteEntry(uniqueId) {
      try {
        await clipboardService.deleteEntry(uniqueId);
        
        // Remove entry from list
        this.entries = this.entries.filter(entry => entry.unique_id !== uniqueId);
      } catch (err) {
        console.error('Error deleting entry', err);
        alert('Failed to delete entry. Please try again.');
      }
    },
    
    copyToClipboard(text, uniqueId) {
      navigator.clipboard.writeText(text)
        .then(() => {
          this.copiedEntries = {
            ...this.copiedEntries,
            [uniqueId]: true
          };
          
          setTimeout(() => {
            this.copiedEntries = {
              ...this.copiedEntries,
              [uniqueId]: false
            };
          }, 2000);
        })
        .catch(err => {
          console.error('Failed to copy', err);
          alert('Failed to copy to clipboard. Please try again.');
        });
    },
    
    copyClipboardLink() {
      navigator.clipboard.writeText(this.currentUrl)
        .then(() => {
          this.linkCopied = true;
          
          setTimeout(() => {
            this.linkCopied = false;
          }, 2000);
        })
        .catch(err => {
          console.error('Failed to copy link', err);
          alert('Failed to copy link. Please try again.');
        });
    },
    
    confirmDelete(uniqueId) {
      if (confirm('Are you sure you want to delete this entry?')) {
        this.deleteEntry(uniqueId);
      }
    },

    startEditingName() {
      this.editNameValue = this.clipboardName;
      this.isEditingName = true;
      this.$nextTick(() => {
        this.$refs.nameInput.focus();
      });
    },

    async updateClipboardName() {
      const newName = this.editNameValue.trim();
      if (!newName) {
        return;
      }

      try {
        const response = await clipboardService.updateClipboard(this.clipboardId, {
          name: newName
        });
        
        this.clipboardName = response.data.clipboard.name;
        this.isEditingName = false;
      } catch (err) {
        console.error('Error updating clipboard name', err);
        alert('Failed to update name. Please try again.');
      }
    },

    cancelEditingName() {
      this.isEditingName = false;
      this.editNameValue = '';
    },
    
    startEditingContent() {
      this.editContentValue = this.clipboardContent;
      this.isEditingContent = true;
      this.$nextTick(() => {
        this.$refs.contentTextarea && this.$refs.contentTextarea.focus();
      });
    },

    async updateClipboardContent() {
      const newContent = this.editContentValue.trim();
      
      try {
        const response = await clipboardService.updateClipboard(this.clipboardId, {
          content: newContent
        });
        
        this.clipboardContent = response.data.clipboard.content;
        this.isEditingContent = false;
      } catch (err) {
        console.error('Error updating clipboard content', err);
        alert('Failed to update description. Please try again.');
      }
    },

    cancelEditingContent() {
      this.isEditingContent = false;
      this.editContentValue = '';
    },
    
    onReady(cm) {
      console.log("CodeMirror ready");
      if (cm) {
        cm.focus();
        this.setupAutocomplete(cm);
      }
    },

    onLangChange() {
      // Only update the editor directly, don't mutate reactive data
      if (this.$refs.cmRef && this.$refs.cmRef.editor) {
        this.$refs.cmRef.editor.setOption('mode', this.selectedLang);
      }
    },

    getDisplayOptions(contentType) {
      return {
        mode: contentType || 'markdown',
        lineNumbers: false,
        lineWrapping: true,
        theme: 'default',
        tabSize: 4,
        readOnly: true,
        styleActiveLine: false,
        cursorBlinkRate: -1, // Hide cursor
        scrollbarStyle: 'native'
      };
    },

    getModalDisplayOptions(contentType) {
      return {
        mode: contentType || 'markdown',
        lineNumbers: false,
        lineWrapping: true,
        theme: 'default',
        tabSize: 4,
        readOnly: true,
        styleActiveLine: false,
        cursorBlinkRate: -1, // Hide cursor
        scrollbarStyle: 'native'
      };
    },

    openEditModal(entry) {
      this.modalEntry = entry;
      this.modalEntryContent = entry.content;
      this.modalEntryName = entry.name || '';
      this.modalSelectedLang = entry.content_type || 'markdown';
      this.saveButtonState = 'normal'; // Reset button state
      // Don't mutate modalCmOptions here - let the template handle mode updates
      this.modalVisible = true;
      // Prevent body scrolling when modal is open
      document.body.style.overflow = 'hidden';
      
      // Add global style for hints in modal context
      this.$nextTick(() => {
        this.addModalHintsStyle();
      });
    },

    closeModal() {
      // If this is an expand editor, transfer content back to main editor (not save to database)
      if (this.modalEntry && this.modalEntry.id === 'temp-expand') {
        this.newContent = this.modalEntryContent;
        this.newEntryName = this.modalEntryName; // Transfer the entry name back
        this.selectedLang = this.modalSelectedLang;
        
        // Update the main editor's mode if it exists
        if (this.$refs.cmRef && this.$refs.cmRef.editor) {
          this.$refs.cmRef.editor.setOption('mode', this.selectedLang);
        }
      }
      
      this.modalVisible = false;
      this.modalEntry = null;
      this.modalEntryContent = '';
      this.modalEntryName = '';
      this.modalSelectedLang = 'markdown';
      // Restore body scrolling
      document.body.style.overflow = '';
      // Remove modal hints style
      this.removeModalHintsStyle();
    },

    async saveModalEntry() {
      if (!this.modalEntryContent.trim()) {
        return;
      }
      
      this.saveButtonState = 'saving';
      
      try {
        // Check if this is a temporary expand editor entry
        if (this.modalEntry.id === 'temp-expand') {
          // Save to database like the main editor save button
          const response = await clipboardService.addEntry(
            this.clipboardId,
            this.modalEntryContent,
            this.modalSelectedLang
          );
          
          // Add the new entry to the list
          this.entries.unshift(response.data);
          
          // Clear the main editor content since it's now saved
          this.newContent = '';
          
          // Update the main editor's mode if it exists
          if (this.$refs.cmRef && this.$refs.cmRef.editor) {
            this.$refs.cmRef.editor.setOption('mode', this.selectedLang);
          }
          
          // Show success state briefly then close modal
          this.saveButtonState = 'success';
          setTimeout(() => {
            this.saveButtonState = 'normal';
          }, 1000);
          
          // Close modal after saving to database
          setTimeout(() => {
            this.modalVisible = false;
            this.modalEntry = null;
            this.modalEntryContent = '';
            this.modalSelectedLang = 'markdown';
            document.body.style.overflow = '';
            this.removeModalHintsStyle();
          }, 1200);
          
          return;
        }
        
        // Normal entry update
        const response = await clipboardService.updateEntry(
          this.modalEntry.unique_id, 
          this.modalEntryContent, 
          this.modalSelectedLang,
          this.modalEntryName.trim()
        );
        
        // Update the entry in the list
        const index = this.entries.findIndex(e => e.unique_id === this.modalEntry.unique_id);
        if (index !== -1) {
          this.entries[index] = response.data;
        }
        
        // Show success state
        this.saveButtonState = 'success';
        
        // Reset to normal after 2 seconds
        setTimeout(() => {
          this.saveButtonState = 'normal';
        }, 2000);
      } catch (err) {
        console.error('Error updating entry', err);
        this.saveButtonState = 'normal';
        alert('Failed to update entry. Please try again.');
      }
    },
    
    onModalLangChange() {
      // Only update the editor directly, don't mutate reactive data
      if (this.$refs.modalCmRef && this.$refs.modalCmRef.editor) {
        this.$refs.modalCmRef.editor.setOption('mode', this.modalSelectedLang);
      }
    },

    previewInNewTab() {
      // For existing entries, open EntryView
      if (this.modalEntry && this.modalEntry.unique_id) {
        this.viewEntry(this.modalEntry.unique_id);
      }
    },

    // JSON formatting methods
    formatJSON() {
      const result = jsonService.format(this.modalEntryContent);
      this.handleJSONResult(result, 'JSON formatted successfully');
    },

    compressJSON() {
      const result = jsonService.compress(this.modalEntryContent);
      this.handleJSONResult(result, 'JSON compressed successfully');
    },

    escapeJSON() {
      const result = jsonService.escape(this.modalEntryContent);
      this.handleJSONResult(result, 'JSON escaped successfully');
    },

    unescapeJSON() {
      const result = jsonService.unescape(this.modalEntryContent);
      this.handleJSONResult(result, 'JSON unescaped successfully');
    },

    handleJSONResult(result, successMessage) {
      if (result.success) {
        // Update content
        this.modalEntryContent = result.data;
        
        // Update CodeMirror editor
        this.$nextTick(() => {
          if (this.$refs.modalCmRef && this.$refs.modalCmRef.editor) {
            const editor = this.$refs.modalCmRef.editor;
            editor.setValue(result.data);
            editor.clearHistory(); // Clear undo history after formatting
          }
        });
        
        // Show success message
        this.showNotification(successMessage, 'success');
      } else {
        // Show error message
        this.showNotification(result.error, 'error');
      }
    },

    showNotification(message, type = 'info') {
      // Remove any existing notification first
      const existingNotification = document.querySelector('.json-notification');
      if (existingNotification) {
        existingNotification.remove();
      }

      // Create notification element
      const notification = document.createElement('div');
      notification.className = `json-notification ${type}`;
      notification.textContent = message;
      
      // Style the notification - position in modal footer area, left-aligned
      notification.style.cssText = `
        padding: 8px 16px;
        margin: 0;
        font-size: 13px;
        font-weight: 500;
        z-index: 1;
        text-align: left;
        display: block;
        width: 100%;
        box-sizing: border-box;
        border-top: 1px solid #e9ecef;
        transition: all 0.3s ease;
        ${type === 'success' ? 
          'background: #d4edda; color: #155724;' : 
          'background: #f8d7da; color: #721c24;'
        }
      `;
      
      // Find modal footer and add notification to it
      const modalFooter = document.querySelector('.modal-footer');
      if (modalFooter) {
        // Insert notification at the beginning of footer for left alignment
        modalFooter.insertBefore(notification, modalFooter.firstChild);
      } else {
        // Fallback: find modal content and add notification to it
        const modalContent = document.querySelector('.modal-content');
        if (modalContent) {
          // Insert notification at the beginning of modal content
          modalContent.insertBefore(notification, modalContent.firstChild);
        } else {
          // Final fallback to body if modal not found
          document.body.appendChild(notification);
        }
      }
      
      // Auto remove after 3 seconds
      setTimeout(() => {
        if (notification.parentNode && notification.parentNode.contains(notification)) {
          notification.style.opacity = '0';
          notification.style.transform = 'translateY(-10px)';
          setTimeout(() => {
            if (notification.parentNode && notification.parentNode.contains(notification)) {
              notification.parentNode.removeChild(notification);
            }
          }, 300);
        }
      }, 3000);
    },

    openExpandEditor() {
      // Create a temporary entry for the expand editor
      const tempEntry = {
        id: 'temp-expand',
        content: this.newContent,
        content_type: this.selectedLang,
        name: this.newEntryName, // Include the entry name
        created_at: new Date().toISOString()
      };
      
      this.modalEntry = tempEntry;
      this.modalEntryContent = this.newContent;
      this.modalEntryName = this.newEntryName; // Transfer the entry name
      this.modalSelectedLang = this.selectedLang;
      this.saveButtonState = 'normal';
      // Don't mutate modalCmOptions here - let the template handle mode updates
      this.modalVisible = true;
      
      // Prevent body scrolling when modal is open
      document.body.style.overflow = 'hidden';
      
      // Add global style for hints in modal context
      this.$nextTick(() => {
        this.addModalHintsStyle();
      });
    },

    onModalReady(cm) {
      console.log("Modal CodeMirror ready");
      if (cm) {
        cm.focus();
        this.setupAutocomplete(cm);
      }
    },

    getModalEditOptions(contentType) {
      return {
        mode: contentType || 'markdown',
        lineNumbers: true,
        lineWrapping: true,
        theme: 'default',
        tabSize: 4,
        readOnly: false,
        styleActiveLine: true,
        scrollbarStyle: 'native'
      };
    },

    getLanguageLabel(contentType) {
      const lang = this.langOptions.find(opt => opt.value === contentType);
      return lang ? lang.label : contentType;
    },

    getLanguageColor(contentType) {
      const colors = {
        'markdown': '#0969da',
        'json': '#d156f9',
        'sql': '#26c0e3',
        'clike': '#16325c',
        'shell': '#89e051',
        'javascript': '#d156f9',
        'htmlmixed': '#e34c26',
        'css': '#1572b6',
        'xml': '#ff6600',
        'python': '#4ca998',
        'yaml': '#cb171e',
        'groovy': '#4298b8'
      };
      return colors[contentType] || '#42b983';
    },



    async switchToTextTab() {
      this.activeTab = 'text';
      // Refresh text entries when switching to text tab
      try {
        const entriesResponse = await clipboardService.getEntries(this.clipboardId);
        this.entries = entriesResponse.data;
        // Also refresh available files in case new files were uploaded
        await this.updateAvailableFiles();
      } catch (err) {
        console.error('Error refreshing text entries', err);
      }
    },

    async switchToFilesTab() {
      this.activeTab = 'files';
      // Files will be refreshed by the FileUploader component when it becomes visible
      // We can trigger a refresh by emitting an event or using a ref
      this.$nextTick(() => {
        // The FileUploader component will automatically refresh when it becomes visible
        if (this.$refs.fileUploader && this.$refs.fileUploader.fetchFiles) {
          this.$refs.fileUploader.fetchFiles();
        }
      });
    },

    goHome() {
      // Open home page in a new tab
      const homeUrl = window.location.origin;
      window.open(homeUrl, '_blank');
    },

    viewEntry(uniqueId) {
      const url = this.$router.resolve({ name: 'EntryView', params: { uniqueId } }).href;
      window.open(url, '_blank');
    },

    updateFileCount(count) {
      this.fileCount = count;
    },



    // Removed markdown rendering methods for security
    // All content is now displayed in CodeMirror editors only

    // Update available files when switching tabs or loading data
    async updateAvailableFiles() {
      try {
        const response = await fileService.getFiles(this.clipboardId);
        this.availableFiles = response.data || [];
        // Update markdown service with new file list
        markdownService.setAvailableFiles(this.availableFiles);
      } catch (error) {
        console.error('Error fetching available files:', error);
        this.availableFiles = [];
        markdownService.setAvailableFiles([]);
      }
    },

    // Setup autocomplete functionality for CodeMirror
    setupAutocomplete(cm) {
      // Register custom hint function
      cm.setOption('hintOptions', {
        hint: this.fileHint.bind(this),
        completeSingle: false,
        closeOnUnfocus: true
      });

      // Listen for input events
      cm.on('inputRead', (instance, changeObj) => {
        if (changeObj.text && changeObj.text[0]) {
          // Only enable autocomplete in markdown mode
          const currentMode = instance.getOption('mode');
          console.log(currentMode);
          if (currentMode !== 'markdown') {
            return;
          }
          
          const cursor = instance.getCursor();
          const line = instance.getLine(cursor.line);
          const before = line.substring(0, cursor.ch);
          
          // Check if we're typing after @ (only support @file: now)
          const atMatch = before.match(/@(file)?:?([^@\s]*)$/);
          if (atMatch) {
            // Trigger autocomplete
            setTimeout(() => {
              instance.showHint();
              // Fix z-index for modal context
              this.fixHintsZIndex();
            }, 100);
          }
        }
      });
      
      // Listen for hint selection to trigger file list
      cm.on('endCompletion', (instance) => {
        // Check if the user just selected @file: and trigger file autocomplete
        setTimeout(() => {
          const cursor = instance.getCursor();
          const line = instance.getLine(cursor.line);
          const before = line.substring(0, cursor.ch);
          
          // If line ends with @file: immediately show file suggestions
          if (before.endsWith('@file:')) {
            instance.showHint();
            this.fixHintsZIndex();
          }
        }, 50);
      });

      // Handle escape key to close autocomplete
      cm.addKeyMap({
        'Esc': (instance) => {
          if (instance.state.completionActive) {
            instance.state.completionActive.close();
          }
          return false;
        }
      });
    },

    // Custom hint function for file references
    fileHint(cm) {
      // Only provide hints in markdown mode
      const currentMode = cm.getOption('mode');
      if (currentMode !== 'markdown') {
        return null;
      }
      
      const cursor = cm.getCursor();
      const line = cm.getLine(cursor.line);
      const before = line.substring(0, cursor.ch);
      
      // Match @file: or just @
      const atMatch = before.match(/@(file)?:?([^@\s]*)$/);
      if (!atMatch) {
        return null;
      }

      const [fullMatch, type, partial] = atMatch;
      const start = cursor.ch - fullMatch.length;
      const end = cursor.ch;

      let suggestions = [];

      if (!type) {
        // Just @ was typed, show unified file option
        suggestions = [
          {
            text: '@file:',
            displayText: '@file: - Insert File Reference (images will preview, others will download)',
            className: 'autocomplete-type'
          }
        ];
      } else {
        // Filter files based on partial match (show all files for @file:)
        const filteredFiles = this.availableFiles.filter(file => {
          return file.filename.toLowerCase().includes(partial.toLowerCase());
        });

        suggestions = markdownService.generateFileSuggestions(filteredFiles, partial);
      }

      if (suggestions.length === 0) {
        return null;
      }

      return {
        list: suggestions,
        from: { line: cursor.line, ch: start },
        to: { line: cursor.line, ch: end }
      };
    },



    // Fix z-index for hints when in modal context
    fixHintsZIndex() {
      this.$nextTick(() => {
        const hints = document.querySelector('.CodeMirror-hints');
        if (hints && this.modalVisible) {
          hints.style.zIndex = '20000';
        }
      });
    },

    // Add global style for hints in modal context
    addModalHintsStyle() {
      // Remove existing style if present
      this.removeModalHintsStyle();
      
      const style = document.createElement('style');
      style.id = 'modal-hints-style';
      style.textContent = `
        .CodeMirror-hints {
          z-index: 20000 !important;
        }
      `;
      document.head.appendChild(style);
    },

    // Remove modal hints style
    removeModalHintsStyle() {
      const existingStyle = document.getElementById('modal-hints-style');
      if (existingStyle) {
        existingStyle.remove();
      }
    },

    // Helper function to convert plain @file: references to proper markdown syntax
    convertToMarkdownSyntax(content) {
      if (!content) return content;
      
      // Convert plain @file:filename to [filename](@file:filename) for non-images
      // This will match @file: that is not preceded by ](
      content = content.replace(/(?<!\]\()@file:([^\s\]]+)/g, '[$1](@file:$1)');
      
      return content;
    }
  }
}
</script>

<style scoped>
.clipboard-view {
  max-width: 800px;
  margin: 0 auto;
  padding: 20px;
}

.clipboard-container {
  background-color: white;
  border-radius: 8px;
  padding: 25px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
}

.loading, .error {
  text-align: center;
  padding: 40px;
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
}

.clipboard-header {
  margin-bottom: 20px;
  padding-bottom: 15px;
  border-bottom: 1px solid #eee;
}

.header-top {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 10px;
}

.header-title {
  flex: 1;
}

.home-btn-top {
  padding: 8px;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  background-color: #42b983;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  box-shadow: 0 2px 6px rgba(66, 185, 131, 0.2);
}

.home-btn-top:hover {
  background-color: #36996b;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(66, 185, 131, 0.3);
}

.clipboard-info {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 10px;
  font-size: 14px;
  color: #666;
}

.clipboard-id {
  font-weight: bold;
  color: #42b983;
}

h1 {
  color: #2c3e50;
  margin-bottom: 5px;
}

h2 {
  color: #2c3e50;
  margin-top: 30px;
  margin-bottom: 15px;
}

.form-container {
  display: flex;
  flex-direction: column;
  margin-bottom: 30px;
  border: 2px dashed #ddd;
  padding: 25px;
  transition: background-color 0.3s;
  border-radius: 8px;
}

.editor-container {
  width: 100%;
  border-radius: 5px;
  overflow: hidden;
  margin-bottom: 10px;
}

.content-input {
  display: none; /* Hide the original textarea if still in the DOM */
}

.save-button {
  padding: 12px 20px;
  background-color: #42b983;
  color: white;
  border: none;
  border-radius: 5px;
  font-size: 16px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.save-button:hover:not(.disabled) {
  background-color: #3aa876;
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(66, 185, 131, 0.3);
}

.save-button.disabled {
  background-color: #cccccc;
  color: #999999;
  cursor: not-allowed;
  opacity: 0.6;
}

.save-button.active {
  background-color: #42b983;
  box-shadow: 0 2px 4px rgba(66, 185, 131, 0.2);
}

.copy-link-btn, .home-btn {
  padding: 6px 12px;
  font-size: 14px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
}

.copy-link-btn {
  background-color: #42b983;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 40px;
  transition: background-color 0.2s ease;
}

.copy-link-btn:hover {
  background-color: #36996b;
}

.home-btn {
  background-color: #e6e6e6;
  color: #333;
}

.entries-list {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.entry-card {
  border: 1px solid #e8f5e8;
  border-radius: 5px;
  padding: 15px;
  background-color: #f9f9f9;
  transition: all 0.3s ease;
}

.entry-card:hover {
  border-color: #42b983;
  box-shadow: 0 2px 8px rgba(66, 185, 131, 0.15);
  transform: translateY(-1px);
}

.entry-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 10px;
  border-bottom: 1px solid #eee;
  padding-bottom: 10px;
  gap: 12px;
}

.entry-info {
  display: flex;
  flex-direction: column;
  gap: 6px;
  flex: 1;
  min-width: 0; /* Allow shrinking */
}

.entry-title-row {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0; /* Allow shrinking */
  flex-wrap: wrap; /* Allow wrapping on very small screens */
}

.entry-name {
  font-weight: 600;
  color: #2c3e50;
  font-size: 14px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 300px; /* Limit maximum width */
}

.entry-type {
  color: white;
  font-size: 11px;
  font-weight: 600;
  padding: 3px 8px;
  border-radius: 12px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
}

.entry-date {
  color: #999;
  font-size: 12px;
  font-weight: 400;
}

.entry-actions {
  display: flex;
  gap: 5px;
  flex-shrink: 0; /* Prevent shrinking */
}

.copy-button {
  padding: 5px 8px;
  border: none;
  border-radius: 3px;
  cursor: pointer;
  font-size: 14px;
  background-color: #42b983;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.2s ease;
}

.copy-button:hover {
  background-color: #36996b;
}

.delete-button {
  padding: 5px 8px;
  border: none;
  border-radius: 3px;
  cursor: pointer;
  font-size: 14px;
  background-color: #e74c3c;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.2s ease;
}

.delete-button:hover {
  background-color: #c0392b;
}

.view-button {
  padding: 5px 8px;
  border: none;
  border-radius: 3px;
  background-color: #3498db;
  color: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 30px;
  height: 30px;
  transition: background-color 0.2s ease;
}

.view-button:hover {
  background-color: #2980b9;
}

.entry-content {
  font-size: 16px;
  line-height: 1.6;
  word-break: break-word;
}

.entry-content-display {
  max-height: 100px;
  overflow: hidden;
  cursor: pointer;
  transition: background-color 0.2s ease;
  border-radius: 4px;
}

.entry-content-display:hover {
  background-color: rgba(66, 185, 131, 0.05);
}

/* Removed markdown-rendered styles for security */

.empty-message {
  text-align: center;
  color: #666;
  padding: 20px;
  background-color: #f9f9f9;
  border-radius: 5px;
  border: 1px dashed #ddd;
}

.copy-success {
  color: white;
  font-weight: bold;
  display: flex;
  align-items: center;
  gap: 4px;
}

.checkmark {
  stroke: white;
  animation: checkmark 0.3s ease-in-out forwards;
}

@keyframes checkmark {
  0% {
    transform: scale(0);
    opacity: 0;
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
}

.copy-button {
  display: flex;
  align-items: center;
  justify-content: center;
  /* min-width: 70px; */
}

.link-container {
  display: flex;
  align-items: center;
  gap: 10px;
}

.link-preview {
  padding: 10px;
  background-color: #f9f9f9;
  border: 1px solid #ddd;
  border-radius: 5px;
  flex-grow: 1;
}

.tabs-navigation {
  display: flex;
  margin-bottom: 0;
  padding: 4px;
  width: 100%;
  background-color: #f8f9fa;
  border-radius: 12px;
  height: auto;
  align-items: center;
  border: 1px solid #e9ecef;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.tab-button {
  height: 40px;
  padding: 0 24px;
  border: none;
  border-radius: 8px;
  background-color: transparent;
  color: #6c757d;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.3s ease;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 2px;
  min-width: 100px;
  flex: 1;
}

.tab-button.active {
  background-color: #42b983;
  color: white;
  font-weight: 600;
  box-shadow: 0 2px 8px rgba(66, 185, 131, 0.3);
  transform: translateY(-1px);
}

.tab-button:not(.active):hover {
  background-color: #e9ecef;
  color: #495057;
  transform: translateY(-0.5px);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.tab-label {
  margin-left: 8px;
  font-size: 14px;
}

.tab-content {
  animation: fadeIn 0.3s ease-in-out;
  padding: 24px;
  border-radius: 12px;
  background-color: #ffffff;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.05);
  border: 1px solid #e9ecef;
  margin-top: 8px;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.clipboard-name {
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 8px;
  border-radius: 6px;
  transition: background-color 0.3s ease;
  margin: -4px -8px;
}

.clipboard-name:hover {
  background-color: rgba(66, 185, 131, 0.05);
}

.edit-icon {
  margin-left: 8px;
  opacity: 0.4;
  transition: all 0.3s ease;
  color: #6c757d;
  display: inline-flex;
  align-items: center;
  padding: 4px;
  border-radius: 4px;
  background-color: transparent;
}

.clipboard-name:hover .edit-icon {
  opacity: 1;
  color: #42b983;
  background-color: rgba(66, 185, 131, 0.1);
  transform: scale(1.1);
}

.edit-name-form {
  width: 100%;
  margin-bottom: 15px;
}

.edit-input-container {
  display: flex;
  flex-direction: column;
  gap: 10px;
  max-width: calc(100% - 60px); /* Leave space for home button if needed */
}

.edit-name-input {
  font-size: 24px;
  padding: 8px 12px;
  border: 2px solid #42b983;
  border-radius: 5px;
  width: 100%;
}

.edit-actions {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

.save-btn, .cancel-btn {
  padding: 8px 16px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
}

.save-btn {
  background-color: #42b983;
  color: white;
}

.cancel-btn {
  background-color: #e6e6e6;
  color: #333;
}

.edit-button {
  padding: 5px 8px;
  border: none;
  border-radius: 3px;
  cursor: pointer;
  font-size: 14px;
  background-color: #3498db;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.2s ease;
}

.edit-button:hover {
  background-color: #2980b9;
}

.view-button {
  padding: 5px 8px;
  border: none;
  border-radius: 3px;
  cursor: pointer;
  font-size: 14px;
  background-color: #17a2b8;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.2s ease;
}

.view-button:hover {
  background-color: #138496;
}

.entry-edit-form {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.edit-entry-textarea {
  display: none; /* Hide the original textarea if still in the DOM */
}

.edit-entry-actions {
  display: flex;
  gap: 8px;
  justify-content: flex-end;
}

/* Ensure CodeMirror appears properly */
:deep(.cm-s-default) {
  border: 1px solid #ddd;
  border-radius: 5px;
  font-size: 16px;
  font-family: monospace;
}

:deep(.CodeMirror) {
  height: 200px;
  line-height: 1.6;
}

:deep(.CodeMirror-placeholder) {
  color: #999;
}

:deep(.cm-keyword) {
  color: #07a;
}

:deep(.cm-string) {
  color: #690;
}

:deep(.cm-comment) {
  color: #999;
  font-style: italic;
}

/* Styles for read-only display CodeMirror */
.entry-content :deep(.CodeMirror) {
  height: 100px !important;
  max-height: 100px !important;
  background-color: #f8f9fa;
  border: 1px solid #e9ecef;
  font-size: 14px;
}

.entry-content :deep(.CodeMirror-scroll) {
  overflow-y: auto;
  overflow-x: auto;
}

.entry-content :deep(.CodeMirror-lines) {
  padding: 8px 0;
}

.entry-content :deep(.CodeMirror-readonly-cursor) {
  display: none;
}

.editor-toolbar {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 6px;
}

.toolbar-flex {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 6px;
}
.toolbar-left {
  display: flex;
  align-items: center;
  gap: 10px;
}
.toolbar-right {
  display: flex;
  align-items: center;
  gap: 8px;
}
.toolbar-btn {
  padding: 8px;
  background-color: #42b983;
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.2s, box-shadow 0.2s;
  box-shadow: 0 2px 8px rgba(66, 185, 131, 0.08);
  display: flex;
  align-items: center;
  justify-content: center;
  /* min-width: 40px;
  height: 40px; */
}
.toolbar-btn:hover:not(.disabled) {
  background-color: #36996b;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(66, 185, 131, 0.2);
}

.toolbar-btn.disabled {
  background-color: #cccccc !important;
  color: #999999 !important;
  cursor: not-allowed !important;
  opacity: 0.6 !important;
  box-shadow: 0 2px 8px rgba(204, 204, 204, 0.08) !important;
}

.toolbar-btn.active {
  background-color: #42b983;
  box-shadow: 0 2px 8px rgba(66, 185, 131, 0.2);
}

.expand-button {
  background-color: #3498db;
}

.expand-button:hover {
  background-color: #2980b9;
}

/* Modal Styles */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.modal-content {
  background: white;
  border-radius: 8px;
  width: 90%;
  height: 80%;
  max-width: 1200px;
  max-height: 800px;
  display: flex;
  flex-direction: column;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 16px 20px;
  border-bottom: 1px solid #e9ecef;
  background-color: #f8f9fa;
  border-radius: 8px 8px 0 0;
  gap: 12px;
}

.modal-header h3 {
  margin: 0;
  color: #2c3e50;
  font-size: 18px;
  text-transform: capitalize;
}

.modal-close {
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  color: #6c757d;
  transition: all 0.2s ease;
  flex-shrink: 0; /* Prevent shrinking */
  margin-top: 2px; /* Align with the title section */
}

.modal-close:hover {
  background-color: #e9ecef;
  color: #495057;
}

.modal-body {
  flex: 1;
  padding: 0;
  overflow: hidden;
  height: calc(100% - 120px); /* Account for header and footer */
}

.modal-body :deep(.CodeMirror) {
  height: 100% !important;
  border: none;
  border-radius: 0 0 8px 8px;
  font-size: 14px;
}

.modal-body :deep(.CodeMirror-scroll) {
  height: 100%;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 16px 20px;
  border-top: 1px solid #e9ecef;
  background-color: #f8f9fa;
  border-radius: 0 0 8px 8px;
}

.modal-save-btn, .modal-cancel-btn {
  padding: 8px 20px;
  border: none;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.modal-save-btn {
  background-color: #42b983;
  color: white;
}

.modal-save-btn:hover {
  background-color: #36996b;
}

.modal-cancel-btn {
  background-color: #6c757d;
  color: white;
}

.modal-cancel-btn:hover {
  background-color: #5a6268;
}

.modal-title-section {
  flex: 1;
  min-width: 0; /* Allow shrinking */
  margin-right: 12px; /* Add margin to prevent overlap with close button */
}

.modal-controls-row {
  display: flex;
  align-items: center;
  gap: 15px;
}

.preview-toggle-btn {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  background: #007bff;
  border: 1px solid #007bff;
  border-radius: 4px;
  color: white;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 1px 2px rgba(0, 123, 255, 0.2);
  height: 28px;
}

.preview-toggle-btn:hover {
  background: #0056b3;
  border-color: #0056b3;
  box-shadow: 0 2px 4px rgba(0, 123, 255, 0.3);
}

.preview-toggle-btn.active {
  background: #28a745;
  border-color: #28a745;
  color: white;
  box-shadow: 0 1px 2px rgba(40, 167, 69, 0.3);
}

.preview-toggle-btn.active:hover {
  background: #218838;
  border-color: #218838;
  box-shadow: 0 2px 4px rgba(40, 167, 69, 0.4);
}

/* JSON formatting controls */
.json-controls {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.json-btn {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  border: 1px solid;
  border-radius: 4px;
  color: white;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  height: 28px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
}

.json-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

.format-btn {
  background: #17a2b8;
  border-color: #17a2b8;
}

.format-btn:hover {
  background: #138496;
  border-color: #138496;
}

.compress-btn {
  background: #6f42c1;
  border-color: #6f42c1;
}

.compress-btn:hover {
  background: #5a359a;
  border-color: #5a359a;
}

.escape-btn {
  background: #fd7e14;
  border-color: #fd7e14;
}

.escape-btn:hover {
  background: #e56500;
  border-color: #e56500;
}

.unescape-btn {
  background: #20c997;
  border-color: #20c997;
}

.unescape-btn:hover {
  background: #1ba085;
  border-color: #1ba085;
}

.modal-lang-selector {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
}

.modal-lang-selector label {
  color: #6c757d;
  font-weight: 500;
}

.modal-lang-selector select {
  padding: 4px 8px;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  font-size: 13px;
}

/* Save Button States */
.modal-save-btn.saving {
  background-color: #6c757d;
  cursor: not-allowed;
}

.modal-save-btn.success {
  background-color: #28a745;
}

.spinner {
  display: inline-block;
  width: 12px;
  height: 12px;
  border: 2px solid transparent;
  border-top: 2px solid currentColor;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-right: 4px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Clipboard Content Section Styles */
.clipboard-content-section {
  margin-top: 15px;
  padding-top: 15px;
  border-top: 1px solid #eee;
}

.content-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
}

.content-header h3 {
  margin: 0;
  font-size: 16px;
  color: #2c3e50;
  font-weight: 600;
}

.edit-content-btn {
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  color: #6c757d;
  transition: all 0.2s ease;
}

.edit-content-btn:hover {
  background-color: rgba(66, 185, 131, 0.1);
  color: #42b983;
}

.content-display p {
  margin: 0;
  color: #555;
  line-height: 1.5;
  background-color: #f8f9fa;
  padding: 12px;
  border-radius: 6px;
  border: 1px solid #e9ecef;
}

.content-edit {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.content-textarea {
  width: 100%;
  padding: 12px;
  border: 2px solid #e9ecef;
  border-radius: 6px;
  font-size: 14px;
  line-height: 1.5;
  resize: vertical;
  font-family: inherit;
  transition: border-color 0.2s ease;
}

.content-textarea:focus {
  outline: none;
  border-color: #42b983;
  box-shadow: 0 0 0 3px rgba(66, 185, 131, 0.1);
}

.content-actions {
  display: flex;
  gap: 8px;
  justify-content: flex-start;
}

.save-content-btn, .cancel-content-btn {
  padding: 6px 16px;
  border: none;
  border-radius: 4px;
  font-size: 14px;
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.save-content-btn {
  background-color: #42b983;
  color: white;
}

.save-content-btn:hover {
  background-color: #36996b;
}

.cancel-content-btn {
  background-color: #6c757d;
  color: white;
}

.cancel-content-btn:hover {
  background-color: #5a6268;
}

.add-content-btn {
  background: none;
  border: 1px dashed #ccc;
  padding: 10px 16px;
  border-radius: 6px;
  color: #6c757d;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
}

.add-content-btn:hover {
  border-color: #42b983;
  color: #42b983;
  background-color: rgba(66, 185, 131, 0.05);
}

/* Entry Name Input Styles */
.entry-name-input {
  margin-bottom: 10px;
}

.name-input {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #e9ecef;
  border-radius: 4px;
  font-size: 14px;
  transition: border-color 0.2s ease;
}

.name-input:focus {
  outline: none;
  border-color: #42b983;
  box-shadow: 0 0 0 2px rgba(66, 185, 131, 0.1);
}

/* Modal Name Input Styles */
.modal-name-input {
  margin-bottom: 10px;
}

.modal-name-field {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #e9ecef;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 500;
  transition: border-color 0.2s ease;
  box-sizing: border-box; /* Include padding and border in width calculation */
}

.modal-name-field:focus {
  outline: none;
  border-color: #42b983;
  box-shadow: 0 0 0 2px rgba(66, 185, 131, 0.1);
}

/* Removed all markdown rendered styles for security */

/* CodeMirror Autocomplete Styles */
:deep(.CodeMirror-hints) {
  position: absolute;
  z-index: 20000 !important; /* Higher than modal overlay (10000) */
  overflow: hidden;
  list-style: none;
  margin: 0;
  padding: 0;
  background: white;
  border: 1px solid #ddd;
  border-radius: 6px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  max-height: 200px;
  overflow-y: auto;
  font-family: inherit;
  font-size: 14px;
}

/* Ensure hints appear above modal when in modal context */
.modal-overlay :deep(.CodeMirror-hints) {
  z-index: 20000 !important;
}

.preview-modal-overlay :deep(.CodeMirror-hints) {
  z-index: 20000 !important;
}

:deep(.CodeMirror-hint) {
  margin: 0;
  padding: 8px 12px;
  border-radius: 0;
  white-space: nowrap;
  color: #333;
  cursor: pointer;
  transition: background-color 0.2s ease;
  border-bottom: 1px solid #f0f0f0;
  display: flex;
  align-items: center;
  gap: 8px;
}

:deep(.CodeMirror-hint:last-child) {
  border-bottom: none;
}

:deep(.CodeMirror-hint:hover) {
  background-color: #f8f9fa;
}

:deep(.CodeMirror-hint.CodeMirror-hint-active) {
  background-color: #42b983 !important;
  color: white !important;
}

/* Custom autocomplete item styles */
:deep(.autocomplete-type) {
  background-color: #e3f2fd;
  color: #1976d2;
  font-weight: 600;
}

:deep(.autocomplete-type:hover) {
  background-color: #bbdefb;
}

:deep(.autocomplete-type.CodeMirror-hint-active) {
  background-color: #1976d2 !important;
  color: white !important;
}

:deep(.autocomplete-image) {
  background-color: #f3e5f5;
  color: #7b1fa2;
}

:deep(.autocomplete-image:hover) {
  background-color: #e1bee7;
}

:deep(.autocomplete-image.CodeMirror-hint-active) {
  background-color: #7b1fa2 !important;
  color: white !important;
}

:deep(.autocomplete-file-link) {
  background-color: #e8f5e8;
  color: #2e7d32;
}

:deep(.autocomplete-file-link:hover) {
  background-color: #c8e6c9;
}

:deep(.autocomplete-file-link.CodeMirror-hint-active) {
  background-color: #2e7d32 !important;
  color: white !important;
}

/* Add icons for different file types */
:deep(.autocomplete-type::before) {
  content: "📝";
  margin-right: 6px;
}

:deep(.autocomplete-image::before) {
  content: "🖼️";
  margin-right: 6px;
}

:deep(.autocomplete-file-link::before) {
  content: "📎";
  margin-right: 6px;
}

/* Improve scrollbar styling */
:deep(.CodeMirror-hints::-webkit-scrollbar) {
  width: 6px;
}

:deep(.CodeMirror-hints::-webkit-scrollbar-track) {
  background: #f1f1f1;
  border-radius: 3px;
}

:deep(.CodeMirror-hints::-webkit-scrollbar-thumb) {
  background: #c1c1c1;
  border-radius: 3px;
}

:deep(.CodeMirror-hints::-webkit-scrollbar-thumb:hover) {
  background: #a8a8a8;
}

/* Modal preview content styles */
.modal-preview-content {
  height: 100%;
  overflow-y: auto;
  padding: 20px;
  background: white;
  border: 1px solid #e9ecef;
  border-radius: 4px;
}

/* Removed markdown preview styles for security */

.modal-preview-content::-webkit-scrollbar {
  width: 8px;
}

.modal-preview-content::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

.modal-preview-content::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 4px;
}

.modal-preview-content::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .entry-name {
    max-width: 200px; /* Smaller max-width on mobile */
  }
  
  .modal-header {
    padding: 12px 16px; /* Reduce padding on mobile */
  }
  
  .modal-title-section {
    margin-right: 8px; /* Reduce margin on mobile */
  }
}

@media (max-width: 480px) {
  .entry-name {
    max-width: 150px; /* Even smaller on very small screens */
  }
  
  .entry-title-row {
    gap: 8px; /* Reduce gap on small screens */
  }
}
</style> 